import type { IReportKeys } from '@mindverse/tracker';
import {
  reportClick as _reportClick,
  reportCustomError as _reportCustomError,
  reportExpose as _reportExpose,
  reportOther as _reportOther
} from '@mindverse/tracker';

type ReportType = 'click' | 'expose' | 'other' | 'error';

interface QueueItem {
  type: ReportType;
  key: string;
  params: IReportKeys;
  retryCount?: number;
}

const MAX_RETRY_COUNT = 3;

class ReportQueue {
  private static instance: ReportQueue;
  private queue: QueueItem[] = [];
  private failedQueue: QueueItem[] = [];
  private isReady = false;
  private checkInterval: ReturnType<typeof setInterval> | null = null;

  private constructor() {
    this.init();
  }

  static getInstance(): ReportQueue {
    if (!ReportQueue.instance) {
      ReportQueue.instance = new ReportQueue();
    }

    return ReportQueue.instance;
  }

  private init(): void {
    // Check if tracker script is loaded by checking for the global tracker object
    this.checkInterval = setInterval(() => {
      if (this.isTrackerReady()) {
        this.setReady();
      }
    }, 100);

    // Also listen for window load event as a fallback
    if (typeof window !== 'undefined') {
      window.addEventListener('load', () => {
        // Give extra time after load for lazyOnload scripts
        setTimeout(() => {
          if (!this.isReady) {
            this.setReady();
          }
        }, 1000);
      });

      // Listen for online event to retry failed reports
      window.addEventListener('online', () => {
        console.log('[ReportQueue] Network online, retrying failed reports...');
        this.retryFailedReports();
      });
    }
  }

  private isTrackerReady(): boolean {
    // Check if the external tracker script has initialized
    // The tracker script injects global functions like _MINDVERSE_TRACKER_REPORT_EXPOSE_
    if (typeof window !== 'undefined') {
      return (
        typeof (window as Record<string, unknown>)._MINDVERSE_TRACKER_REPORT_EXPOSE_ === 'function'
      );
    }

    return false;
  }

  private setReady(): void {
    if (this.isReady) return;

    console.log('[ReportQueue] SDK ready, flushing queue...');
    this.isReady = true;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.flush();
  }

  private flush(): void {
    console.log('[ReportQueue] Flushing', this.queue.length, 'items');

    while (this.queue.length > 0) {
      const item = this.queue.shift();

      if (item) {
        this.executeReport(item);
      }
    }
  }

  private addToFailedQueue(item: QueueItem): void {
    const retryCount = (item.retryCount || 0) + 1;

    if (retryCount < MAX_RETRY_COUNT) {
      this.failedQueue.push({ ...item, retryCount });
      console.log(
        '[ReportQueue] Added to failed queue, retry count:',
        retryCount,
        'queue size:',
        this.failedQueue.length
      );
    } else {
      console.log('[ReportQueue] Max retry count reached, dropping item:', item.type, item.key);
    }
  }

  private retryFailedReports(): void {
    if (this.failedQueue.length === 0) return;

    console.log('[ReportQueue] Retrying', this.failedQueue.length, 'failed reports');

    const itemsToRetry = [...this.failedQueue];

    this.failedQueue = [];

    for (const item of itemsToRetry) {
      this.executeReport(item);
    }
  }

  private executeReport(item: QueueItem): void {
    const { type, key, params } = item;

    console.log('[ReportQueue] executing report:', type, key, params);

    try {
      switch (type) {
        case 'click':
          _reportClick(key, params);

          break;
        case 'expose':
          _reportExpose(key, params);

          break;
        case 'other':
          _reportOther(key, params);

          break;
        case 'error':
          _reportCustomError(key, params);

          break;
      }
    } catch (e) {
      console.error('[ReportQueue] Report failed:', e);
      this.addToFailedQueue(item);
    }
  }

  report(type: ReportType, key: string, params: IReportKeys): void {
    console.log('[ReportQueue] report called:', type, key, 'isReady:', this.isReady);

    // First, try to retry any failed reports
    if (this.isReady && this.failedQueue.length > 0) {
      this.retryFailedReports();
    }

    if (this.isReady) {
      this.executeReport({ type, key, params });
    } else {
      this.queue.push({ type, key, params });
      console.log('[ReportQueue] queued, queue size:', this.queue.length);
    }
  }
}

export const reportQueue = ReportQueue.getInstance();

export function queueReportClick(key: string, params: IReportKeys): void {
  reportQueue.report('click', key, params);
}

export function queueReportExpose(key: string, params: IReportKeys): void {
  reportQueue.report('expose', key, params);
}

export function queueReportOther(key: string, params: IReportKeys): void {
  reportQueue.report('other', key, params);
}

export function queueReportCustomError(key: string, params: IReportKeys): void {
  reportQueue.report('error', key, params);
}
